import React, { useEffect, useState } from 'react';

import TpDialog from '../shared/tp_dialog';
import StatusPill from '../shared/status_pill';
import LoadFailed from '../shared/load_failed';
import { fmt } from '../format';
import { post_base_rate } from '../api';
import {
  NOTE_MAX,
  NOTE_MIN,
  can_save,
  day_label,
  form_errors,
  is_opening,
  note_length,
  preview_text,
  refusal_text,
  request_body,
  saved_text,
  stamp_label,
  status_view
} from './logic';

/*
 * PROD-7828 R13 — the base rate history pop-up: the append-only history plus, for users who may change
 * rates, the add form. The parent owns the history fetch (it also drives the profile's rate line).
 */
const EMPTY_FORM = { rate: '', effective_date: '', note: '' };

const HistoryTable = ({ entries }) => {
  if (!entries.length) return <div className="tp-empty">No rate history yet.</div>;
  return (
    <div className="tp-tablewrap">
      <table className="tp-table">
        <thead>
          <tr>
            <th scope="col">Rate (per 60 min)</th>
            <th scope="col">Effective from (IST)</th>
            <th scope="col">Status</th>
            <th scope="col">Note</th>
            <th scope="col">Changed by</th>
            <th scope="col">Changed at (IST)</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => {
            const s = status_view(e.status);
            return (
              <tr key={e.uuid}>
                <td className="tp-strong">{fmt(e.base_rate)}</td>
                <td>{day_label(e.effective_from_ist)}</td>
                <td>
                  <StatusPill tone={s.tone}>{s.label}</StatusPill>
                </td>
                <td style={{ whiteSpace: 'pre-wrap', maxWidth: 280 }}>
                  {is_opening(e) ? 'Opening rate' : e.note}
                </td>
                <td>
                  <div>{e.created_by_name || '—'}</div>
                  {e.created_by_email ? <div className="tp-hint">{e.created_by_email}</div> : null}
                </td>
                <td>{stamp_label(e.created_at_ist)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const AddForm = ({ teacher_uuid, data, busy, locked, set_busy, on_dirty, on_saved, now }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [touched, setTouched] = useState({});
  const [refusal, setRefusal] = useState('');
  const [saved, setSaved] = useState('');

  const bounds = {
    earliest_allowed_date: data.earliest_allowed_date,
    latest_allowed_date: data.latest_allowed_date
  };
  const errors = form_errors(form, bounds);
  const valid = can_save(form, bounds);
  const preview = valid ? preview_text(data.entries, form, now) : '';
  const dirty = form.rate !== '' || form.effective_date !== '' || form.note !== '';
  useEffect(() => on_dirty(dirty), [dirty, on_dirty]);

  const set_field = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setRefusal('');
    setSaved('');
  };
  const touch = (field) => setTouched((t) => ({ ...t, [field]: true }));
  // An error shows once the field was left, or as soon as it holds something.
  const err = (field) => (touched[field] || form[field] !== '' ? errors[field] : '');

  const submit = (e) => {
    e.preventDefault();
    if (!valid || busy || locked) return;
    set_busy(true);
    setRefusal('');
    post_base_rate(request_body(teacher_uuid, form))
      .then((result) => {
        const text = saved_text(result, form);
        setSaved(text);
        setForm(EMPTY_FORM);
        setTouched({});
        on_saved(text);
      })
      .catch((e2) => {
        console.error('base rate save failed', e2?.response?.status || e2?.message);
        setRefusal(refusal_text(e2));
      })
      .finally(() => set_busy(false));
  };

  const field_id = (f) => `brh-${f}`;
  const described = (f, hint) =>
    [hint ? `${field_id(f)}-hint` : '', err(f) ? `${field_id(f)}-err` : '']
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <form className="tp-form" onSubmit={submit} aria-label="Add a rate change" noValidate>
      <h4
        style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 700, color: 'var(--brand-primary)' }}
      >
        Add a rate change
      </h4>
      <fieldset disabled={busy || locked} style={{ border: 0, padding: 0, margin: 0 }}>
        <div className="tp-field-2">
          <div className="tp-field">
            <label htmlFor={field_id('rate')}>New rate (₹ per 60 min)</label>
            <input
              id={field_id('rate')}
              className="tp-input"
              inputMode="numeric"
              required
              aria-invalid={err('rate') ? 'true' : undefined}
              aria-describedby={described('rate')}
              value={form.rate}
              onChange={(e) => set_field('rate', e.target.value)}
              onBlur={() => touch('rate')}
              placeholder="e.g. 900"
            />
            {err('rate') ? (
              <span id={`${field_id('rate')}-err`} className="tp-err">
                {err('rate')}
              </span>
            ) : null}
          </div>
          <div className="tp-field">
            <label htmlFor={field_id('effective_date')}>Effective from (00:00 IST)</label>
            <input
              id={field_id('effective_date')}
              type="date"
              className="tp-input"
              required
              min={data.earliest_allowed_date || undefined}
              max={data.latest_allowed_date || undefined}
              aria-invalid={err('effective_date') ? 'true' : undefined}
              aria-describedby={described('effective_date', true)}
              value={form.effective_date}
              onChange={(e) => set_field('effective_date', e.target.value)}
              onBlur={() => touch('effective_date')}
            />
            <span id={`${field_id('effective_date')}-hint`} className="tp-hint">
              {day_label(data.earliest_allowed_date)} to {day_label(data.latest_allowed_date)}.
              Closed months stay frozen.
            </span>
            {err('effective_date') ? (
              <span id={`${field_id('effective_date')}-err`} className="tp-err">
                {err('effective_date')}
              </span>
            ) : null}
          </div>
        </div>
        <div className="tp-field">
          <label htmlFor={field_id('note')}>Note (why the rate changes)</label>
          <textarea
            id={field_id('note')}
            className="tp-textarea"
            required
            maxLength={NOTE_MAX}
            aria-invalid={err('note') ? 'true' : undefined}
            aria-describedby={described('note', true)}
            value={form.note}
            onChange={(e) => set_field('note', e.target.value)}
            onBlur={() => touch('note')}
            placeholder="e.g. Annual revision approved by Ops"
          />
          <span id={`${field_id('note')}-hint`} className="tp-hint">
            {note_length(form.note)}/{NOTE_MAX} characters, at least {NOTE_MIN}.
          </span>
          {err('note') ? (
            <span id={`${field_id('note')}-err`} className="tp-err">
              {err('note')}
            </span>
          ) : null}
        </div>
      </fieldset>

      {preview ? (
        <div className="tp-banner tp-banner-info" data-testid="brh-preview">
          {preview}
        </div>
      ) : null}
      {refusal ? (
        <div className="tp-banner tp-banner-error" role="alert">
          {refusal}
        </div>
      ) : null}
      {saved ? (
        <div className="tp-banner tp-banner-ok" role="status">
          {saved}
        </div>
      ) : null}

      <div className="tp-modal-ft">
        <button
          type="button"
          className="tp-btn-out"
          disabled={busy || locked || !dirty}
          onClick={() => {
            setForm(EMPTY_FORM);
            setTouched({});
            setRefusal('');
          }}
        >
          Clear
        </button>
        <button type="submit" className="tp-btn" disabled={!valid || busy || locked}>
          {busy ? 'Saving…' : 'Save rate change'}
        </button>
      </div>
    </form>
  );
};

const BaseRateHistoryDialog = ({
  teacher_uuid,
  teacher_name,
  data,
  loading,
  error,
  on_retry,
  on_saved,
  on_close,
  now
}) => {
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const entries = (data && Array.isArray(data.entries) && data.entries) || [];
  const title = teacher_name ? `Base rate history: ${teacher_name}` : 'Base rate history';
  return (
    <TpDialog
      title={title}
      busy={busy}
      guard_text={
        dirty ? 'You have an unsaved rate change. Save or clear it, or use ✕ to discard it.' : ''
      }
      on_close={on_close}
    >
      <div className="tp-modal tp-modal-wide">
        {data ? (
          <p style={{ margin: '0 0 12px', fontSize: 13 }}>
            Current rate: <span className="tp-strong">{fmt(data.current_rate)}</span> per 60 min.
            Each class uses the rate in effect at its start time (IST). Entries can't be edited or
            removed; a new entry for the same date replaces the earlier one.
          </p>
        ) : null}
        {error ? <LoadFailed text={error} on_retry={on_retry} /> : null}
        {!data && loading ? (
          <div className="tp-empty" role="status">
            Loading rate history…
          </div>
        ) : null}
        {data ? <HistoryTable entries={entries} /> : null}
        {loading && data ? (
          <div className="tp-hint" role="status">
            Refreshing…
          </div>
        ) : null}
        {/* Shown only once a load confirms can_change; locked (read-only) while a reload is in flight. */}
        {data && data.can_change ? (
          <div style={{ marginTop: 16 }}>
            <AddForm
              teacher_uuid={teacher_uuid}
              data={data}
              busy={busy}
              locked={loading}
              set_busy={setBusy}
              on_dirty={setDirty}
              on_saved={on_saved}
              now={now}
            />
          </div>
        ) : null}
      </div>
    </TpDialog>
  );
};

export default BaseRateHistoryDialog;
